import { ReactNode, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { GoogleOAuthProvider } from '@react-oauth/google'
import * as Sentry from '@sentry/react'
import SidebarMenu from './SidebarMenu/SidebarMenu'
import { StyledSidebarMenu } from './SidebarMenu/styled-components'
import { config } from '@/containers/Login/config'
import Login from '@/containers/Login'
import ErrorPage from '@/containers/ErrorPage'
import { selectAuthData } from '@/containers/Login/AuthSelectors'
import i18n from '@/i18n'
import { capitalize } from '@/shared/tools/stringFunctions'
import { applicationCountries } from '@/shared/tools/applicationCountries'
import { useNotificationReceivedListener } from './hooks/useNotificationReceivedListener'
import useOutdatedSessionLogout from './hooks/useOutdatedSessionLogout'
import useCutOffersListener from './hooks/useCutOffersListener'
import GamificationNotification from '@/shared/components/GamificationNotification/GamificationNotification'
import CutOffersBanner from '@/shared/components/CutOffersBanner/CutOffersBanner'
import HopTimusButton from './HopTimusButton/HopTimusButton'
import HopAssistanceChat from './HopAssistanceChat/HopAssistanceChat'
import { AppShell, AppShellWarning, BannerArea, SidebarArea, MainContent } from '@/shared/theme/styled-components'

const COUNTRY_USING_ONLY_HOP_TIMUS = 'fr'

interface AuthData {
  authTokenSession: string | null
  isAdmin: boolean
  isSelectraAdmin: boolean
  country: string
  userLanguage: string
  applicationType: string
}

interface BackofficePageProps {
  children: ReactNode
}

const BackofficePage = ({ children }: BackofficePageProps) => {
  const {
    authTokenSession,
    isAdmin,
    isSelectraAdmin,
    country,
    userLanguage,
    applicationType: applicationTypeAuth,
  } = useSelector(selectAuthData) as AuthData
  const { gamificationNotification, clearGamificationNotification } = useNotificationReceivedListener()
  useOutdatedSessionLogout()
  // Subscribe to the team-scoped Echo channel that pushes the live list of
  // offers currently in a cut period. Sleeps until teamId is available.
  useCutOffersListener()

  const locationHook = useLocation()
  const [loggedIn, setLoggedIn] = useState(!!authTokenSession)
  const [applicationType, setApplicationType] = useState(applicationTypeAuth)
  const authorizedApplicationTypes = [
    'telecom',
    'energie',
    'users-mngt',
    'teams-mngt',
    'suppliers-mngt',
    'websales-users-mngt',
    'ip-addresses-management',
    'assurance',
    'contact',
    'callbacks',
    'gestione-pause',
    'dev-tools-v2',
    'additional-sales',
    'organisations',
  ]
  const allowedCountries = Object.keys(applicationCountries)
  const hasErrorType = !authorizedApplicationTypes.includes(applicationType)
  const hasErrorCountry = !allowedCountries.includes(country)
  const isWarning = location.hostname.includes('localhost') || location.hostname.includes('staging')
  const Wrapper = isWarning ? AppShellWarning : AppShell

  useEffect(() => {
    if (!userLanguage || !country) return
    i18n.changeLanguage(`${country}${capitalize(userLanguage)}`)
  }, [userLanguage, country])

  useEffect(() => {
    if (authTokenSession) {
      setLoggedIn(true)
    } else {
      setLoggedIn(false)
    }
  }, [authTokenSession])

  useEffect(() => {
    const splitedPath = locationHook.pathname.split('/')
    const currentType = splitedPath[1] ?? ''

    if (applicationType !== currentType) {
      setApplicationType(currentType)
    }
  }, [locationHook.pathname])

  if (!loggedIn) {
    return (
      <GoogleOAuthProvider
        clientId={config.clientId}
        onScriptLoadError={() => {
          if (import.meta.env.PROD) {
            Sentry.captureException(new Error('Google OAuth script failed to load'))
          }
        }}
      >
        <Login />
      </GoogleOAuthProvider>
    )
  }

  if (hasErrorType || hasErrorCountry) {
    return (
      <Wrapper>
        <BannerArea>
          <CutOffersBanner />
        </BannerArea>
        <SidebarArea id='sidebar-menu'>
          <StyledSidebarMenu>
            <SidebarMenu
              applicationType={applicationType}
              isAdmin={isAdmin ?? false}
              isSelectraAdmin={isSelectraAdmin ?? false}
              error={true}
            />
          </StyledSidebarMenu>
        </SidebarArea>
        <ErrorPage
          hasErrorType={hasErrorType}
          hasErrorCountry={hasErrorCountry}
          currentType={applicationType}
          currentCountry={country}
        />
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      <BannerArea>
        <CutOffersBanner />
      </BannerArea>
      <SidebarArea id='sidebar-menu'>
        <StyledSidebarMenu>
          <SidebarMenu
            applicationType={applicationType}
            isAdmin={isAdmin ?? false}
            isSelectraAdmin={isSelectraAdmin ?? false}
            error={false}
          />
        </StyledSidebarMenu>
      </SidebarArea>

      <MainContent id='main'>
        <section>{children}</section>
      </MainContent>
      <GamificationNotification notification={gamificationNotification} onClose={clearGamificationNotification} />
      <HopTimusButton />
      <HopAssistanceChat hidden={country === COUNTRY_USING_ONLY_HOP_TIMUS} />
    </Wrapper>
  )
}

export default BackofficePage
